// constants
import { StoreStorageSyncKeys } from 'components/mrp/mrp.consts';

// types
import type { CalendarSourceData, CalendarStore } from './calendarStore.types';

// utils
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  getNewCalendarId,
  getNextCalendarOrder,
  getSourceEntityIds,
  reorderCalendars,
  withMappings
} from './calendarStore.utils';
import dayjs from 'dayjs';
import { getSortedCalendars } from 'components/mrp/mrp.utils';

export const useCalendarStore = create<CalendarStore>()(
  persist(
    (set) => ({
      calendars: {},
      calendarIdsPerEntityId: {},
      entityIdsPerCalendarId: {},
      hasHydrated: false,
      timeSlotMin: dayjs()
        .startOf('day')
        .hour(7)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(), // 7:00
      timeSlotMax: dayjs()
        .startOf('day')
        .hour(19)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate(), // 19:00
      timeStep: 30,
      viewState: 'APPOINTMENT',
      control: {
        openCalendar: (sources, asPinned) =>
          set((state) => {
            const sourcesEntityIds = getSourceEntityIds(sources);

            const alreadyOpened = Object.values(state.calendars).some(
              (calendar) => {
                if (calendar.sources.length !== sources.length) return false;

                const calendarEntityIds = getSourceEntityIds(calendar.sources);

                return sourcesEntityIds.every(
                  (srcId) => calendarEntityIds.indexOf(srcId) != -1
                );
              }
            );

            // Don't open calendars that are already opened
            if (alreadyOpened) return state;

            const calendar = {
              calendarId: getNewCalendarId(state.calendars),
              sources,
              isPinned: asPinned,
              order: getNextCalendarOrder(state.calendars)
            };

            const calendars = {
              ...state.calendars,
              [calendar.calendarId]: calendar
            };

            return withMappings(calendars);
          }),
        closeCalendar: (calendarId, forceClose) =>
          set((state) => {
            const targetCalendar = state.calendars?.[calendarId];

            // Check if calendar can/should be closed
            if (
              !targetCalendar || // Calendar couldnt be found
              (targetCalendar.isPinned && !forceClose) // Calendar was pinned and force is false
            )
              return state;

            // Close the calendar
            const updatedCalendars = Object.fromEntries(
              Object.entries(state.calendars)
                .filter(([id]) => id != calendarId)
                .map(([id, calendar]) => [id, calendar])
            );

            return withMappings(updatedCalendars);
          }),
        replaceCalendar: (
          targetCalendarId: string,
          sources: CalendarSourceData[]
        ) =>
          set((state) => {
            const targetCalendar = state.calendars?.[targetCalendarId];

            if (!targetCalendar) return state;

            // Replace the calendar with a new one
            const newCalendar = {
              ...targetCalendar,
              calendarId: getNewCalendarId(state.calendars),
              sources,
              order:
                targetCalendar.order ?? getNextCalendarOrder(state.calendars)
            };

            const updatedCalendars = {
              ...state.calendars,
              [newCalendar.calendarId]: newCalendar
            };

            // Remove the old calendar
            delete updatedCalendars[targetCalendarId];

            return withMappings(updatedCalendars);
          }),
        mergeCalendars: (calendarIds) =>
          set((state) => {
            const firstCalendar = state.calendars[calendarIds[0]];
            const targetCalendarList = Object.values(state.calendars).filter(
              (calendar) => calendarIds.includes(calendar.calendarId)
            );
            const remainingCalendars = Object.fromEntries(
              Object.entries(state.calendars).filter(
                ([calendarId]) => !calendarIds.includes(calendarId)
              )
            );

            if (!firstCalendar || targetCalendarList.length < 2) return state;

            // Merge all the data into new calendar based on the first calendar.
            const newCalendar = {
              calendarId: getNewCalendarId(state.calendars),
              sources: targetCalendarList.flatMap(
                (calendar) => calendar.sources
              ),
              isPinned: true,
              order: firstCalendar.order
            };

            const newCalendars = {
              ...remainingCalendars,
              [newCalendar.calendarId]: newCalendar
            };

            return withMappings(newCalendars);
          }),
        splitCalendar: (calendarId, entityId) =>
          set((state) => {
            const targetCalendar = state.calendars[calendarId];
            if (!targetCalendar) return state;

            const { sources } = targetCalendar;
            const matchingSources = entityId
              ? sources.filter(
                  (src) => src.planningEntityIdentifier.entityId === entityId
                )
              : [];

            if (entityId && matchingSources.length === 0) return state;

            // Create array of source arrays for new calendars.
            const newSources = entityId
              ? [
                  sources.filter(
                    (src) => src.planningEntityIdentifier.entityId !== entityId
                  ),
                  matchingSources
                ].filter((sourceGroup) => sourceGroup.length > 0)
              : sources.map((src) => [src]);

            if (entityId && newSources.length < 2) return state;

            const nextCalendarId = Number(getNewCalendarId(state.calendars));
            const newCalendarList = newSources.map((src, idx) => ({
              calendarId: (nextCalendarId + idx).toString(),
              sources: src,
              isPinned: true
            }));

            const sortedCalendars = getSortedCalendars(state.calendars);
            const currentIndex = sortedCalendars.indexOf(targetCalendar);
            sortedCalendars.splice(currentIndex, 1, ...newCalendarList);

            const updatedCalendars = reorderCalendars(sortedCalendars);

            return withMappings(updatedCalendars);
          }),
        setTimeSlotMinMax: (type: 'min' | 'max', newValue: Date) =>
          set((state) => {
            const d = dayjs(newValue);
            // Guard against setting min/max outside of the current time slot range
            if (
              !d.isSame(
                type === 'min' ? state.timeSlotMin : state.timeSlotMax,
                'day'
              )
            ) {
              return state;
            }

            return {
              ...state,
              ...(type === 'min'
                ? { timeSlotMin: newValue }
                : type === 'max'
                ? { timeSlotMax: newValue }
                : {})
            };
          }),
        setTimeStep: (timeStep) =>
          set((state) => {
            return {
              ...state,
              timeStep
            };
          }),
        toggleCalendarPin: (calendarId) =>
          set((state) => {
            const targetCalendar = state.calendars[calendarId];

            if (!targetCalendar) return state;

            return {
              ...state,
              calendars: {
                ...state.calendars,
                [calendarId]: {
                  ...targetCalendar,
                  isPinned: !targetCalendar.isPinned
                }
              }
            };
          }),
        moveCalendar: (calendarId, newPosition) =>
          set((state) => {
            const targetCalendar = state.calendars[calendarId];

            if (!targetCalendar) return state;

            const sortedCalendars = getSortedCalendars(state.calendars);
            const currentCalendarIdx = sortedCalendars.indexOf(targetCalendar);
            // Remove the current calendar from the list before inserting it at the new position

            sortedCalendars.splice(currentCalendarIdx, 1);
            switch (newPosition) {
              case 'MAX':
                sortedCalendars.push(targetCalendar);
                break;
              case 'MIN':
                sortedCalendars.unshift(targetCalendar);
                break;
              default:
                const newIndex = currentCalendarIdx + newPosition;
                sortedCalendars.splice(newIndex, 0, targetCalendar);
            }

            const updatedCalendars = reorderCalendars(sortedCalendars);

            return withMappings(updatedCalendars);
          }),
        setViewState: (viewState) => set(() => ({ viewState }))
      }
    }),
    {
      name: StoreStorageSyncKeys.CALENDAR_STORE,
      partialize: (state) => ({
        calendars: state.calendars,
        calendarIdsPerEntityId: state.calendarIdsPerEntityId,
        entityIdsPerCalendarId: state.entityIdsPerCalendarId,
        timeSlotMin: state.timeSlotMin,
        timeSlotMax: state.timeSlotMax,
        timeStep: state.timeStep
      }),
      merge: (persistedState, currentState) => {
        const untransformedState = persistedState as Partial<CalendarStore>;

        return {
          ...currentState,
          ...untransformedState,
          // Transform persisted date strings back to Dayjs objects
          timeSlotMin: dayjs(untransformedState?.timeSlotMin).isValid()
            ? dayjs(untransformedState.timeSlotMin).toDate()
            : currentState.timeSlotMin,
          timeSlotMax: dayjs(untransformedState?.timeSlotMax).isValid()
            ? dayjs(untransformedState.timeSlotMax).toDate()
            : currentState.timeSlotMax
        };
      },
      onRehydrateStorage: (state) => {
        state.hasHydrated = true;
      },
      version: 1
    }
  )
);
